import React, { useState } from 'react';
import axios from 'axios';
import { FaUserPlus, FaUser, FaEnvelope, FaLock, FaGraduationCap, FaCheckCircle, FaExclamationCircle, FaUsers } from 'react-icons/fa';

const EnrolStudent = () => {
    // Toggle for student/batch
    const [enrollType, setEnrollType] = useState('student'); // 'student' or 'batch'

    const [formData, setFormData] = useState({
        username: '',
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        academic_year: '',
    });
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) {
            errors.name = 'Full name is required';
        } else if (formData.name.trim().length < 3) {
            errors.name = 'Name must be at least 3 characters long';
        }
        if (!formData.username.trim()) {
            errors.username = 'Username is required';
        } else if (formData.username.trim().length < 3) {
            errors.username = 'Username must be at least 3 characters long';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!emailRegex.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }
        if (!formData.academic_year) {
            errors.academic_year = 'Academic year is required';
        }
        if (!formData.password) {
            errors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            errors.password = 'Password must be at least 8 characters long';
        }
        if (!formData.confirmPassword) {
            errors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setMessage('');
        setIsSubmitting(true);
        if (!validateForm()) {
            setIsSubmitting(false);
            return;
        }
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const data = {
                ...formData,
                type: enrollType,
                role: enrollType === 'student' ? 'student' : 'batch'
            };
            const response = await axios.post(`${apiUrl}/api/signup`, data);
            setMessage(response.data.message);
            setFormData({ username: '', name: '', email: '', password: '', confirmPassword: '', academic_year: '' });
            setValidationErrors({});
        } catch (err) {
            setError(err.response?.data?.error || 'An error occurred during enrollment.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 py-8 px-4">
            <div className="bg-white shadow-2xl rounded-2xl p-8 w-full max-w-2xl">
                {/* Toggle Section */}
                <div className="flex items-center justify-center mb-8 gap-8">
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="radio"
                            name="enrollType"
                            value="student"
                            checked={enrollType === 'student'}
                            onChange={() => setEnrollType('student')}
                            className="form-radio h-5 w-5 text-blue-600"
                        />
                        <span className="ml-2 text-lg font-semibold text-blue-700 flex items-center">
                            <FaUserPlus className="mr-2" /> Enroll Student
                        </span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="radio"
                            name="enrollType"
                            value="batch"
                            checked={enrollType === 'batch'}
                            onChange={() => setEnrollType('batch')}
                            className="form-radio h-5 w-5 text-green-600"
                        />
                        <span className="ml-2 text-lg font-semibold text-green-700 flex items-center">
                            <FaUsers className="mr-2" /> Create Batch
                        </span>
                    </label>
                </div>

                {/* Header Section */}
                <div className="text-center mb-8">
                    <div className={`inline-block p-3 rounded-full ${enrollType === 'student' ? 'bg-gradient-to-r from-blue-500 to-purple-600' : 'bg-gradient-to-r from-green-500 to-blue-400'} mb-4`}>
                        {enrollType === 'student' ? <FaUserPlus className="text-4xl text-white" /> : <FaUsers className="text-4xl text-white" />}
                    </div>
                    <h2 className="text-3xl font-bold text-gray-800 mb-2">
                        {enrollType === 'student' ? 'Enroll a New Student' : 'Create a New Batch'}
                    </h2>
                    <p className="text-gray-600">
                        {enrollType === 'student' ? 'Fill in the details below to enroll a new student' : 'Fill in the details below to create a new batch'}
                    </p>
                </div>

                {/* Alert Messages */}
                {message && (
                    <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-200 flex items-center">
                        <FaCheckCircle className="text-green-500 text-xl mr-3" />
                        <p className="text-green-700">{message}</p>
                    </div>
                )}
                {error && (
                    <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 flex items-center">
                        <FaExclamationCircle className="text-red-500 text-xl mr-3" />
                        <p className="text-red-700">{error}</p>
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Name Field */}
                        <div className="relative">
                            <label className="block text-gray-700 font-semibold mb-2">
                                Full Name <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FaUser className="text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    placeholder={enrollType === 'student' ? "Enter full name" : "Enter batch name"}
                                    className={`w-full pl-10 pr-4 py-3 border ${validationErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-400 placeholder-gray-500`}
                                />
                            </div>
                            {validationErrors.name && (
                                <p className="text-red-500 text-sm mt-1">{validationErrors.name}</p>
                            )}
                        </div>

                        {/* Username Field */}
                        <div className="relative">
                            <label className="block text-gray-700 font-semibold mb-2">
                                Username <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FaUser className="text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleChange}
                                    required
                                    placeholder={enrollType === 'student' ? "Choose a unique username" : "Choose a batch username"}
                                    className={`w-full pl-10 pr-4 py-3 border ${validationErrors.username ? 'border-red-500' : 'border-gray-300'} rounded-lg bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-400 placeholder-gray-500`}
                                />
                            </div>
                            {validationErrors.username && (
                                <p className="text-red-500 text-sm mt-1">{validationErrors.username}</p>
                            )}
                        </div>

                        {/* Email Field */}
                        <div className="relative">
                            <label className="block text-gray-700 font-semibold mb-2">
                                Email Address <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FaEnvelope className="text-gray-400" />
                                </div>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    placeholder={enrollType === 'student' ? "Enter email address" : "Enter batch email"}
                                    className={`w-full pl-10 pr-4 py-3 border ${validationErrors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-400 placeholder-gray-500`}
                                />
                            </div>
                            {validationErrors.email && (
                                <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
                            )}
                        </div>

                        {/* Academic Year Field */}
                        <div className="relative">
                            <label className="block text-gray-700 font-semibold mb-2">
                                Academic Year <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FaGraduationCap className="text-gray-400" />
                                </div>
                                <select
                                    name="academic_year"
                                    value={formData.academic_year}
                                    onChange={handleChange}
                                    required
                                    className={`w-full pl-10 pr-4 py-3 border ${validationErrors.academic_year ? 'border-red-500' : 'border-gray-300'} rounded-lg bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-400 appearance-none`}
                                >
                                    <option value="">Select year</option>
                                    <option value="1">1st Year</option>
                                    <option value="2">2nd Year</option>
                                    <option value="3">3rd Year</option>
                                    <option value="4">4th Year</option>
                                </select>
                            </div>
                            {validationErrors.academic_year && (
                                <p className="text-red-500 text-sm mt-1">{validationErrors.academic_year}</p>
                            )}
                        </div>

                        {/* Password Field */}
                        <div className="relative">
                            <label className="block text-gray-700 font-semibold mb-2">
                                Password <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FaLock className="text-gray-400" />
                                </div>
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                    placeholder={enrollType === 'student' ? "Create a strong password (min. 8 characters)" : "Create a batch password"}
                                    className={`w-full pl-10 pr-4 py-3 border ${validationErrors.password ? 'border-red-500' : 'border-gray-300'} rounded-lg bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-400 placeholder-gray-500`}
                                />
                            </div>
                            {validationErrors.password && (
                                <p className="text-red-500 text-sm mt-1">{validationErrors.password}</p>
                            )}
                        </div>

                        {/* Confirm Password Field */}
                        <div className="relative">
                            <label className="block text-gray-700 font-semibold mb-2">
                                Confirm Password <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FaLock className="text-gray-400" />
                                </div>
                                <input
                                    type="password"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    required
                                    placeholder={enrollType === 'student' ? "Re-enter password" : "Re-enter batch password"}
                                    className={`w-full pl-10 pr-4 py-3 border ${validationErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'} rounded-lg bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-400 placeholder-gray-500`}
                                />
                            </div>
                            {validationErrors.confirmPassword && (
                                <p className="text-red-500 text-sm mt-1">{validationErrors.confirmPassword}</p>
                            )}
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="mt-8">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`w-full py-4 font-bold text-white rounded-lg ${enrollType === 'student' ? 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700' : 'bg-gradient-to-r from-green-500 to-blue-400 hover:from-green-600 hover:to-blue-500'} transition-all duration-300 shadow-lg flex items-center justify-center ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                        >
                            {isSubmitting ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {enrollType === 'student' ? 'Enrolling Student...' : 'Creating Batch...'}
                                </>
                            ) : (
                                <>
                                    {enrollType === 'student' ? <FaUserPlus className="mr-2" /> : <FaUsers className="mr-2" />}
                                    {enrollType === 'student' ? 'Enroll Student' : 'Create Batch'}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EnrolStudent; 